import { useEffect, useRef, useState } from 'react';

const RESET_DELAY_MS = 2000;

/**
 * `copied` flips true for RESET_DELAY_MS after a successful copy, for the
 * "Copy" / "Copied!" button label swap used everywhere something gets
 * copied to the clipboard (a code snippet, a key prefix, a one-time secret,
 * recovery codes).
 *
 * Clears its own timer on unmount — copying right before navigating away
 * used to leave a stray setState-after-unmount warning in three of the four
 * places this was hand-rolled.
 */
export function useCopyToClipboard() {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  async function copy(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), RESET_DELAY_MS);
    } catch {
      // Clipboard access can be denied by the browser; the value stays
      // selectable text at the call site, so this is not a dead end.
    }
  }

  return { copied, copy };
}
